
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CustomRecipe, PantryItem, FavoriteMeal, Language, FoodItem, StorageLocation, PantryCategory } from '../types';
import { t } from '../locales';
import { scanProduct } from '../services/openFoodFactsService';
import { foods as dbFoods } from '../database/food-database';
import { getDecomposition, hasDecomposition } from '../database/decomposition-map';
import { getFoodNameById } from '../constants/autocompleteData';
import { analyzeRecipeImage } from '../services/imageAnalysisService';
import BarcodeScannerModal from './BarcodeScannerModal';
import RecipeModal from './RecipeModal';
import FavoriteNameModal from './FavoriteNameModal';
import AddMethodModal from './AddMethodModal';
import CameraCaptureModal from './CameraCaptureModal';
import SaveProductModal from './SaveProductModal';
import ConfirmationModal from './ConfirmationModal';
import { QuantityEditModal } from './QuantityEditModal';
import { Info, ChevronDown, ChevronRight, Book, Box, Star, Edit2, Trash2, Plus, X, Sparkles, Camera, ArrowLeft, ThermometerSnowflake, Refrigerator, Sun } from 'lucide-react';

interface KitchenModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipes: CustomRecipe[];
  pantryItems: PantryItem[];
  favorites: FavoriteMeal[];
  onSaveRecipe: (recipe: CustomRecipe) => void;
  onDeleteRecipe: (id: string) => void;
  onSavePantryItem: (item: PantryItem) => void;
  onDeletePantryItem: (id: string) => void;
  onDeleteFavorite: (id: string) => void;
  onUpdateFavorite: (favorite: FavoriteMeal) => void;
  language: Language;
}

const KitchenModal: React.FC<KitchenModalProps> = ({ 
    isOpen, onClose, recipes, pantryItems, favorites, onSaveRecipe, onDeleteRecipe, onSavePantryItem, onDeletePantryItem, onDeleteFavorite, onUpdateFavorite, language 
}) => {
  const [activeTab, setActiveTab] = useState<'pantry' | 'cookbook' | 'favorites'>('pantry');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [isFavNameEditOpen, setIsFavNameEditOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  
  // Edit States
  const [editingRecipeItem, setEditingRecipeItem] = useState<FoodItem & { source?: 'home' | 'adapted' } | null>(null);
  const [editingFavoriteData, setEditingFavoriteData] = useState<FavoriteMeal | null>(null);
  const [editingPantryItemData, setEditingPantryItemData] = useState<PantryItem | null>(null);
  
  // Scanning State
  const [scannedProduct, setScannedProduct] = useState<{name: string, barcode: string, ingredients: FoodItem[], categoriesTags: string[]} | null>(null);
  
  // Navigation State for Pantry (Browsing)
  const [selectedLocation, setSelectedLocation] = useState<StorageLocation | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PantryCategory | null>(null);

  const [isNameReadOnly, setIsNameReadOnly] = useState(false);
  const [expandedPantryId, setExpandedPantryId] = useState<string | null>(null);

  const [isAdaptMode, setIsAdaptMode] = useState(false);
  const [adaptSearch, setAdaptSearch] = useState('');
  const [adaptSuggestions, setAdaptSuggestions] = useState<{id: string, name: string}[]>([]);

  // Configuration with translations applied via useMemo
  const locationConfig = useMemo(() => {
      const c: Record<StorageLocation, { label: string, description?: string, icon: React.ReactNode, categories: { id: PantryCategory, label: string, icon: string, examples?: string }[] }> = {
        pantry: {
            label: t(language, 'loc_pantry'),
            description: "Shelf-stable packaged foods stored at room temperature.",
            icon: <Box size={24} />,
            categories: [
                { id: 'canned', label: t(language, 'cat_canned'), icon: "🥫", examples: "tuna, beans, tomato sauce, soups" },
                { id: 'dry', label: t(language, 'cat_dry'), icon: "🍝", examples: "pasta, rice, lentils, flour, oats" },
                { id: 'snacks', label: t(language, 'cat_snacks'), icon: "🍪", examples: "chips, biscuits, bars, chocolate" },
                { id: 'sauces', label: t(language, 'cat_sauces'), icon: "🧂", examples: "ketchup, soy sauce, vinegar, oil" },
                { id: 'jars', label: t(language, 'cat_jars'), icon: "🍯", examples: "honey, pickles, jam" },
                { id: 'beverages', label: t(language, 'cat_beverages'), icon: "☕", examples: "coffee, tea, juice boxes, soda cans" },
                { id: 'bakery', label: t(language, 'cat_bakery'), icon: "🍞", examples: "crackers, toast, wraps" },
                { id: 'misc', label: t(language, 'cat_misc'), icon: "📦" }
            ]
        },
        fridge: {
            label: t(language, 'loc_fridge'),
            description: "Packaged perishable goods requiring refrigeration.",
            icon: <Refrigerator size={24} />,
            categories: [
                { id: 'dairy', label: t(language, 'cat_dairy'), icon: "🥛", examples: "milk, yogurt, cheese, plant drinks" },
                { id: 'meats', label: t(language, 'cat_meats'), icon: "🍗", examples: "ham, sliced chicken, pâté" },
                { id: 'fresh-meals', label: t(language, 'cat_fresh_meals'), icon: "🥗", examples: "ready salads, sandwiches, sushi" },
                { id: 'spreads', label: t(language, 'cat_spreads'), icon: "🧈", examples: "hummus, butter, cheese spreads" },
                { id: 'chilled-drinks', label: t(language, 'cat_chilled_drinks'), icon: "🧃", examples: "smoothies, cold juices" },
                { id: 'desserts', label: t(language, 'cat_desserts'), icon: "🍰", examples: "pudding, mousse, tiramisu" }
            ]
        },
        freezer: {
            label: t(language, 'loc_freezer'),
            description: "Frozen items for long-term storage.",
            icon: <ThermometerSnowflake size={24} />,
            categories: [
                { id: 'frozen-meals', label: t(language, 'cat_frozen_meals'), icon: "🍕", examples: "lasagna, pizza, pasta bowls" },
                { id: 'frozen-produce', label: t(language, 'cat_frozen_produce'), icon: "🥦", examples: "spinach, peas, berries" },
                { id: 'frozen-meats', label: t(language, 'cat_frozen_meats'), icon: "🍖", examples: "chicken, burgers, fish sticks" },
                { id: 'ice-cream', label: t(language, 'cat_ice_cream'), icon: "🍦", examples: "ice cream tubs, sorbet" },
                { id: 'frozen-snacks', label: t(language, 'cat_frozen_snacks'), icon: "🥔", examples: "fries, nuggets, appetizers" }
            ]
        },
        counter: {
            label: t(language, 'loc_counter'),
            description: "Daily items kept visible.",
            icon: <Sun size={24} />,
            categories: [
                { id: 'on-the-go', label: t(language, 'cat_on_the_go'), icon: "🍫", examples: "cereal bars, protein bars" },
                { id: 'ambient-drinks', label: t(language, 'cat_ambient_drinks'), icon: "🧃", examples: "soda, bottled water" },
                { id: 'fresh-bakery', label: t(language, 'cat_fresh_bakery'), icon: "🍞", examples: "croissants, packaged baguettes" }
            ]
        }
      };
      return c;
  }, [language]);

  if (!isOpen) return null;

  const handleBarcodeDetected = async (barcode: string) => {
      setIsScannerOpen(false);
      const product = await scanProduct(barcode);
      if (product) {
          setScannedProduct({
              name: product.name,
              barcode: barcode,
              ingredients: product.ingredients || [],
              categoriesTags: product.categoriesTags || []
          });
      } else {
          alert(t(language, 'noData'));
      }
  };

  const saveScannedProduct = (customName: string, location: StorageLocation, category: PantryCategory) => {
      if (!scannedProduct) return;
      const newItem: PantryItem = {
          id: crypto.randomUUID(),
          name: customName,
          originalName: scannedProduct.name,
          barcode: scannedProduct.barcode,
          ingredients: scannedProduct.ingredients,
          quantity: 1,
          unit: '',
          location: location,
          storageCategory: category
      };
      onSavePantryItem(newItem);
      setScannedProduct(null);
  };

  // --- Recipe Logic ---

  const startCreateRecipeFlow = () => {
      setIsAddMethodOpen(true);
  };

  const handleMethodSelect = (method: 'audio' | 'text' | 'barcode' | 'camera') => {
      setIsAddMethodOpen(false);
      if (method === 'text') {
          handleCreateRecipeManually();
      } else if (method === 'camera') {
          setIsCameraOpen(true);
      }
  };

  const handleCreateRecipeManually = () => {
      setIsNameReadOnly(false);
      setEditingRecipeItem({
          id: 'new',
          name: '', 
          quantity: 4, 
          unit: 'serving',
          timestamp: new Date(),
          ingredients: [],
          isComposite: true,
          source: 'home'
      });
      setIsRecipeModalOpen(true);
  };

  const handleRecipeCapture = async (imageSrc: string) => {
      setIsCameraOpen(false);
      setIsProcessingAI(true);
      
      const parsed = await analyzeRecipeImage(imageSrc, language);
      setIsProcessingAI(false);

      if (parsed) {
          setIsNameReadOnly(false);
          setEditingRecipeItem({
              id: 'new',
              name: parsed.name || 'New Scanned Recipe',
              quantity: parsed.servings || 4,
              unit: 'serving',
              timestamp: new Date(),
              ingredients: parsed.ingredients.map(ing => ({
                  id: crypto.randomUUID(),
                  ...ing,
                  timestamp: new Date()
              })),
              isComposite: true,
              source: 'home'
          });
          setIsRecipeModalOpen(true);
      } else {
          alert("Could not process recipe image. Please try entering ingredients manually.");
      }
  };

  const handleAdaptSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setAdaptSearch(val);
      if (val.length > 1) {
          const lower = val.toLowerCase();
          const matches = dbFoods
            .filter(f => hasDecomposition(f.id) && f[language].toLowerCase().includes(lower))
            .map(f => ({ id: f.id, name: f[language] }))
            .slice(0, 10);
          setAdaptSuggestions(matches);
      } else {
          setAdaptSuggestions([]);
      }
  };

  const handleSelectAdaption = (foodId: string, displayName: string) => {
      const ingIds = getDecomposition(foodId);
      const ingredients = ingIds ? ingIds.map(id => ({
          id: crypto.randomUUID(),
          name: getFoodNameById(id, language) || id,
          quantity: 1,
          unit: 'unit',
          timestamp: new Date()
      })) : [];

      setIsNameReadOnly(false); 
      setEditingRecipeItem({
          id: 'new',
          name: displayName,
          quantity: 4,
          unit: 'serving',
          timestamp: new Date(),
          ingredients: ingredients,
          isComposite: true,
          source: 'adapted'
      });
      
      setIsAdaptMode(false);
      setAdaptSearch('');
      setIsRecipeModalOpen(true);
  };

  const handleEditRecipe = (recipe: CustomRecipe) => {
      setIsNameReadOnly(false); 
      setEditingRecipeItem({
          id: recipe.id,
          name: recipe.name,
          quantity: recipe.servings || 4,
          unit: 'serving',
          timestamp: new Date(),
          ingredients: recipe.ingredients,
          isComposite: true,
          source: recipe.source || 'home'
      });
      setIsRecipeModalOpen(true);
  };

  const handleEditFavoriteItems = (fav: FavoriteMeal) => {
      setEditingFavoriteData(fav);
      setEditingRecipeItem({
          id: fav.id,
          name: fav.name,
          quantity: 1,
          unit: 'serving',
          timestamp: new Date(),
          ingredients: fav.items,
          isComposite: true,
          source: 'home'
      });
      setIsNameReadOnly(true); 
      setIsRecipeModalOpen(true);
  };

  const handleSaveItemsFromRecipeModal = (item: FoodItem, saveAsCustom: boolean, servings?: number) => {
      if (activeTab === 'favorites' && editingFavoriteData) {
          onUpdateFavorite({
              ...editingFavoriteData,
              items: item.ingredients || []
          });
      } else {
          const newRecipe: CustomRecipe = {
              id: item.id === 'new' ? crypto.randomUUID() : item.id,
              name: item.name,
              ingredients: item.ingredients || [],
              servings: servings || item.quantity || 4,
              source: editingRecipeItem?.source || 'home'
          };
          onSaveRecipe(newRecipe);
      }
      setIsRecipeModalOpen(false);
      setEditingRecipeItem(null);
      setEditingFavoriteData(null);
  };

  const togglePantryItem = (id: string) => {
      setExpandedPantryId(expandedPantryId === id ? null : id);
  };

  const handleSavePantryDefaultQuantity = (qty: number, unit: string) => {
      if (!editingPantryItemData) return;
      onSavePantryItem({ ...editingPantryItemData, quantity: qty, unit: unit });
      setEditingPantryItemData(null);
  };

  // --- Deletion Handlers with Custom Modal ---
  const requestDeletePantryItem = (id: string) => {
      setConfirmModal({
          isOpen: true,
          title: t(language, 'deleteItem'),
          message: t(language, 'deleteItemConfirm'),
          onConfirm: () => {
              onDeletePantryItem(id);
              setConfirmModal(null);
          }
      });
  };

  const requestDeleteRecipe = (id: string) => {
      setConfirmModal({
          isOpen: true,
          title: t(language, 'deleteItem'),
          message: t(language, 'deleteItemConfirm'),
          onConfirm: () => {
              onDeleteRecipe(id);
              setConfirmModal(null);
          }
      });
  };

  const requestDeleteFavorite = (id: string) => {
      setConfirmModal({
          isOpen: true,
          title: t(language, 'deleteItem'),
          message: t(language, 'deleteItemConfirm'),
          onConfirm: () => {
              onDeleteFavorite(id);
              setConfirmModal(null);
          }
      });
  };

  // --- Filtering Pantry Items ---
  const filteredPantryItems = pantryItems.filter(item => {
      // Default to 'pantry' and 'misc' if fields missing to support legacy data
      const itemLoc = item.location || 'pantry';
      const itemCat = item.storageCategory || 'misc';

      if (selectedLocation) {
          if (itemLoc !== selectedLocation) return false;
          // If inside a location, check category match
          if (selectedCategory) {
              return itemCat === selectedCategory;
          }
      }
      return true;
  });

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-slideIn">
      <div className="w-full h-full max-w-md mx-auto flex flex-col bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 pt-safe">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                {t(language, 'myKitchen')}
            </h2>
            <button onClick={onClose} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"><X size={24} /></button>
        </div>

        {/* Tabs */}
        <div className="flex p-4 gap-2 bg-gray-50 dark:bg-gray-900/50">
            <button onClick={() => setActiveTab('pantry')} className={`flex-1 py-2 rounded-xl text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === 'pantry' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Box size={14} />
                {t(language, 'myPantry')}
            </button>
            <button onClick={() => setActiveTab('cookbook')} className={`flex-1 py-2 rounded-xl text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === 'cookbook' ? 'bg-[#3F88C5]/10 text-[#3F88C5] dark:bg-[#3F88C5]/30' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Book size={14} />
                {t(language, 'myCookbook')}
            </button>
            <button onClick={() => setActiveTab('favorites')} className={`flex-1 py-2 rounded-xl text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === 'favorites' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Star size={14} />
                {t(language, 'myFavorites')}
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-20 min-h-0">
            
            {/* --- PANTRY TAB --- */}
            {activeTab === 'pantry' && (
                <div className="flex flex-col gap-4 animate-slideIn">
                    
                    {/* Add Button Always Visible */}
                    <button onClick={() => setIsScannerOpen(true)} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2 transition-transform active:scale-95 text-sm">
                        <Camera size={18} />
                        {t(language, 'scanNewProduct')}
                    </button>

                    {/* Navigation Breadcrumbs */}
                    {selectedLocation && (
                        <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-white">
                            <button onClick={() => { setSelectedLocation(null); setSelectedCategory(null); }} className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                                <ArrowLeft size={18} />
                            </button>
                            <span className="capitalize">{locationConfig[selectedLocation].label}</span>
                            {selectedCategory && (
                                <>
                                    <ChevronRight size={14} className="text-gray-400" />
                                    <span className="text-orange-500 capitalize">
                                        {locationConfig[selectedLocation].categories.find(c => c.id === selectedCategory)?.label || selectedCategory}
                                    </span>
                                </>
                            )}
                        </div>
                    )}

                    {/* Level 1: Location Grid (Root Level) */}
                    {!selectedLocation && (
                        <div className="grid grid-cols-2 gap-3">
                            {(Object.keys(locationConfig) as StorageLocation[]).map((locKey) => {
                                const config = locationConfig[locKey];
                                const count = pantryItems.filter(i => (i.location || 'pantry') === locKey).length;
                                return (
                                    <button
                                        key={locKey}
                                        onClick={() => setSelectedLocation(locKey)}
                                        className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-95 aspect-square"
                                    >
                                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300">
                                            {config.icon}
                                        </div>
                                        <span className="font-bold text-sm text-gray-800 dark:text-white">{config.label}</span>
                                        {config.description && <span className="text-[9px] text-gray-400 text-center leading-tight mt-1">{config.description}</span>}
                                        <span className="text-[10px] text-gray-400">{count} items</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Level 2: Category Grid (Specific to selected Location) */}
                    {selectedLocation && !selectedCategory && (
                        <div className="grid grid-cols-2 gap-3">
                            {locationConfig[selectedLocation].categories.map((cat) => {
                                const count = pantryItems.filter(i => (i.location || 'pantry') === selectedLocation && (i.storageCategory || 'misc') === cat.id).length;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all active:scale-95 aspect-square"
                                    >
                                        <span className="text-3xl">{cat.icon}</span>
                                        <span className="font-bold text-sm text-gray-800 dark:text-white text-center">{cat.label}</span>
                                        {cat.examples && <span className="text-[9px] text-gray-400 text-center leading-tight line-clamp-2 mt-0.5">{cat.examples}</span>}
                                        <span className="text-[10px] text-gray-400 mt-1">{count} items</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Level 3: Item List (Leaf Level) */}
                    {selectedLocation && selectedCategory && (
                        <div className="space-y-3">
                            {filteredPantryItems.length === 0 ? (
                                <div className="text-center text-gray-400 text-sm py-8">{t(language, 'pantryEmpty')}</div>
                            ) : (
                                filteredPantryItems.map(item => (
                                    <div key={item.id} className="flex flex-col gap-0 animate-slideIn">
                                        <div onClick={() => togglePantryItem(item.id)} className={`bg-white dark:bg-gray-800 border transition-all duration-300 p-3 rounded-xl flex justify-between items-center shadow-sm cursor-pointer hover:shadow-md ${expandedPantryId === item.id ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-gray-100 dark:border-gray-700'}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="p-1 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-500">{expandedPantryId === item.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
                                                <div><h4 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">{item.name}</h4><p className="text-[9px] text-gray-400 uppercase tracking-tighter font-mono">{item.barcode}</p></div>
                                            </div>
                                            <div className="flex items-center gap-2"><button onClick={(e) => { e.stopPropagation(); setEditingPantryItemData(item); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold transition-all border border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100 flex items-center gap-1 group whitespace-nowrap shadow-sm"><span>{item.quantity || '1'}</span><span className="opacity-60 font-medium">{item.unit || 'unit'}</span></button><button onClick={(e) => { e.stopPropagation(); requestDeletePantryItem(item.id); }} className="p-2 text-gray-300 hover:text-red-500 transition-colors"><Trash2 size={18} /></button></div>
                                        </div>
                                        {expandedPantryId === item.id && (
                                            <div className="bg-orange-50/50 dark:bg-orange-950/20 border-x border-b border-orange-100 dark:border-orange-800/30 rounded-b-xl px-4 py-3 mx-2 animate-slideIn">
                                                <div className="flex items-center gap-2 mb-3 text-orange-600 dark:text-orange-400"><Info size={14} strokeWidth={3} /><h5 className="text-[10px] font-black uppercase tracking-widest">Full Ingredient Breakdown</h5></div>
                                                <div className="flex flex-wrap gap-1.5">{item.ingredients.map((ing, i) => (<div key={i} className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-orange-100 dark:border-orange-900/50 text-gray-800 dark:text-gray-200 rounded-md px-2 py-1 text-[10px] font-medium capitalize shadow-sm"><span>{ing.name}</span></div>))}</div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'cookbook' && (
                <div className="flex flex-col gap-4 animate-slideIn">
                    <div className="flex gap-2">
                        <button onClick={startCreateRecipeFlow} className="flex-1 py-3 bg-[#3F88C5] hover:bg-[#3F88C5]/90 text-white font-bold rounded-lg shadow-md flex items-center justify-center gap-2 transition-transform active:scale-95 text-xs">
                            <Plus size={16} />{t(language, 'createFromScratch')}
                        </button>
                        <button onClick={() => setIsAdaptMode(true)} className="flex-1 py-3 bg-white dark:bg-gray-800 border border-[#3F88C5]/30 text-[#3F88C5] font-bold rounded-lg shadow-sm flex items-center justify-center gap-2 transition-transform active:scale-95 text-xs hover:bg-[#3F88C5]/5">
                            <Book size={16} />{t(language, 'adaptStandard')}
                        </button>
                    </div>
                    {isAdaptMode && (
                        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-[#3F88C5]/20 animate-slideIn">
                            <div className="flex justify-between items-center mb-2"><h4 className="text-xs font-bold text-gray-500 uppercase">{t(language, 'searchStandard')}</h4><button onClick={() => { setIsAdaptMode(false); setAdaptSearch(''); }} className="text-gray-400 hover:text-red-500"><X size={16}/></button></div>
                            <input type="text" value={adaptSearch} onChange={handleAdaptSearchChange} className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm focus:ring-2 focus:ring-[#3F88C5] outline-none" placeholder={t(language, 'searchStandard')} autoFocus />
                            {adaptSuggestions.length > 0 && (<div className="mt-2 flex flex-col gap-1 max-h-40 overflow-y-auto">{adaptSuggestions.map((s, i) => (<button key={i} onClick={() => handleSelectAdaption(s.id, s.name)} className="text-left px-3 py-2 bg-white dark:bg-gray-700 rounded-lg text-sm hover:bg-[#3F88C5]/10 transition-colors">{s.name}</button>))}</div>)}
                        </div>
                    )}
                    <div className="space-y-2">
                        {recipes.length === 0 ? <div className="text-center text-gray-400 text-sm py-8">{t(language, 'noCustomRecipes')}</div> : recipes.map(recipe => (
                            <div key={recipe.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-3 rounded-xl flex justify-between items-center shadow-sm">
                                <div onClick={() => handleEditRecipe(recipe)} className="flex-1 cursor-pointer">
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-bold text-gray-800 dark:text-white">{recipe.name}</h4>
                                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-bold uppercase tracking-tighter border border-blue-100">
                                            {recipe.source === 'home' ? t(language, 'cookbookLabel') : t(language, 'homeRecipeLabel')}
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-gray-400">{recipe.ingredients.length} {t(language, 'ingredientsCount')} • {recipe.servings || 4} {t(language, 'servings').toLowerCase()}</p>
                                </div>
                                <div className="flex items-center gap-1"><button onClick={() => handleEditRecipe(recipe)} className="p-2 text-gray-400 hover:text-[#3F88C5] rounded-full"><Edit2 size={16}/></button><button onClick={(e) => { e.stopPropagation(); requestDeleteRecipe(recipe.id); }} className="p-2 text-gray-300 hover:text-red-500 rounded-full"><Trash2 size={16}/></button></div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'favorites' && (
                <div className="flex flex-col gap-4 animate-slideIn">
                    <div className="space-y-3">
                        {favorites.length === 0 ? <div className="text-center text-gray-400 text-sm py-12">No starred meals yet.<br/>Double-tap a meal in your journal to star it.</div> : favorites.map(fav => (
                            <div key={fav.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-4 rounded-2xl flex justify-between items-center shadow-sm">
                                <div onClick={() => handleEditFavoriteItems(fav)} className="flex-1 cursor-pointer">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-bold text-gray-800 dark:text-white">{fav.name}</h4>
                                        {fav.usualTime && <span className="text-[9px] font-bold bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 px-1.5 py-0.5 rounded border border-yellow-100">{fav.usualTime}</span>}
                                    </div>
                                    <p className="text-[10px] text-gray-400 line-clamp-1">{fav.items.map(i => i.name).join(', ')}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-4">
                                    <button onClick={() => { setEditingFavoriteData(fav); setIsFavNameEditOpen(true); }} className="p-2 text-gray-400 hover:text-yellow-600 rounded-full bg-gray-50 dark:bg-gray-900 transition-colors" title="Edit Name/Time"><Edit2 size={16}/></button>
                                    <button onClick={() => handleEditFavoriteItems(fav)} className="p-2 text-gray-400 hover:text-[#3F88C5] rounded-full bg-gray-50 dark:bg-gray-900 transition-colors" title="Edit Items"><Plus size={16}/></button>
                                    <button onClick={() => requestDeleteFavorite(fav.id)} className="p-2 text-gray-300 hover:text-red-500 rounded-full bg-gray-50 dark:bg-gray-900 transition-colors"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <BarcodeScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onDetected={handleBarcodeDetected} language={language} />

        {/* Use reusable SaveProductModal instead of inline UI for consistency */}
        {scannedProduct && (
            <SaveProductModal 
                isOpen={true} 
                onClose={() => setScannedProduct(null)} 
                product={scannedProduct} 
                onSave={saveScannedProduct} 
                language={language} 
            />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal 
            isOpen={!!confirmModal}
            onClose={() => setConfirmModal(null)}
            onConfirm={confirmModal?.onConfirm || (() => {})}
            title={confirmModal?.title || ''}
            message={confirmModal?.message || ''}
            language={language}
        />

        {editingRecipeItem && (
            <RecipeModal 
                isOpen={isRecipeModalOpen}
                onClose={() => { setIsRecipeModalOpen(false); setEditingRecipeItem(null); setEditingFavoriteData(null); }}
                item={editingRecipeItem}
                onSave={handleSaveItemsFromRecipeModal}
                language={language}
                isNameReadOnly={isNameReadOnly}
                hideSaveOption={true}
                theme={activeTab === 'favorites' ? 'yellow' : 'blue'}
            />
        )}

        {isFavNameEditOpen && editingFavoriteData && (
            <FavoriteNameModal 
                isOpen={isFavNameEditOpen}
                onClose={() => { setIsFavNameEditOpen(false); setEditingFavoriteData(null); }}
                initialName={editingFavoriteData.name}
                initialTime={editingFavoriteData.usualTime}
                language={language}
                onSave={(name, time) => {
                    onUpdateFavorite({ ...editingFavoriteData, name, usualTime: time || undefined });
                    setIsFavNameEditOpen(false);
                    setEditingFavoriteData(null);
                }}
            />
        )}

        {editingPantryItemData && (
            <QuantityEditModal 
                isOpen={!!editingPantryItemData}
                onClose={() => setEditingPantryItemData(null)}
                currentItem={editingPantryItemData}
                onSave={handleSavePantryDefaultQuantity}
                language={language}
                theme={activeTab === 'favorites' ? 'yellow' : 'emerald'}
            />
        )}

        {isAddMethodOpen && (
            <AddMethodModal 
                isOpen={isAddMethodOpen}
                onClose={() => setIsAddMethodOpen(false)}
                onSelect={handleMethodSelect}
                language={language}
                type="recipe"
            />
        )}

        {isCameraOpen && (
            <CameraCaptureModal 
                isOpen={isCameraOpen}
                onClose={() => setIsCameraOpen(false)}
                onCapture={handleRecipeCapture}
                language={language}
            />
        )}

        {isProcessingAI && (
            <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-6">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-2xl flex flex-col items-center animate-popIn max-w-xs text-center border border-white/10">
                    <div className="relative mb-6">
                        <div className="w-16 h-16 border-4 border-[#3F88C5]/20 border-t-[#3F88C5] rounded-full animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center text-[#3F88C5]">
                            <Sparkles size={24} />
                        </div>
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold text-lg mb-2">Analyzing Recipe...</p>
                    <p className="text-xs text-gray-500 leading-relaxed">Gemini is transcribing ingredients and detecting serving sizes.</p>
                </div>
            </div>
        )}

      </div>
    </div>,
    document.body
  );
};

export default KitchenModal;
